// 업데이트 관리 — 업데이트 내역(메인 화면 '업데이트 내역') 추가·수정·삭제 + 화면 하단 버전 표기 변경.
// 권한: 권한관리(auth-admin) 보기 권한 (서버도 같은 기준).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Input, Space, Modal, DatePicker, Popconfirm, Typography, message, Grid,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE } from '../config/api';
import RichEditor from '../components/RichEditor';
import '../styles/Common.css';

const { Text } = Typography;

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const emptyHtml = (html) => !String(html || '').replace(/<(?!img)[^>]*>/g, '').replace(/&nbsp;/g, '').trim();

const UpdatesAdminPage = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === undefined ? window.innerWidth < 768 : !screens.md;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState('');
  const [savedVersion, setSavedVersion] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);

  const [editing, setEditing] = useState(null); // { no?, title, content, write_dt }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/updates/admin');
      setItems(data.items || []);
      setVersion(data.version || '');
      setSavedVersion(data.version || '');
    } catch (e) {
      message.error(e.response?.data?.message || '업데이트 내역을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveVersion = async () => {
    setVersionSaving(true);
    try {
      const { data } = await api.put('/api/updates/admin/version', { version });
      setVersion(data.version);
      setSavedVersion(data.version);
      window.dispatchEvent(new CustomEvent('app-version-changed', { detail: data.version }));
      message.success(`하단 버전을 V${data.version}(으)로 바꿨습니다.`);
    } catch (e) {
      message.error(e.response?.data?.message || '버전 저장에 실패했습니다.');
    } finally {
      setVersionSaving(false);
    }
  };

  const openNew = () => setEditing({ title: '', content: '', write_dt: dayjs() });

  const openEdit = async (no) => {
    try {
      const { data } = await api.get(`/api/updates/admin/${no}`);
      const it = data.item;
      setEditing({ no: it.no, title: it.Title || '', content: it.Content || '', write_dt: it.Write_DT ? dayjs(it.Write_DT) : dayjs() });
    } catch (e) {
      message.error(e.response?.data?.message || '불러오지 못했습니다.');
    }
  };

  const save = async () => {
    if (!editing.title.trim()) { message.warning('제목을 입력하세요.'); return; }
    if (emptyHtml(editing.content)) { message.warning('내용을 입력하세요.'); return; }
    setSaving(true);
    try {
      const body = { title: editing.title.trim(), content: editing.content, write_dt: editing.write_dt ? editing.write_dt.format('YYYY-MM-DD HH:mm') : '' };
      if (editing.no) await api.put(`/api/updates/admin/${editing.no}`, body);
      else await api.post('/api/updates/admin', body);
      message.success(editing.no ? '수정했습니다.' : '등록했습니다.');
      setEditing(null);
      load();
    } catch (e) {
      message.error(e.response?.status === 413 ? '내용이 너무 큽니다. 이미지 수를 줄여 주세요.' : (e.response?.data?.message || '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (no) => {
    try {
      await api.delete(`/api/updates/admin/${no}`);
      message.success('삭제했습니다.');
      load();
    } catch (e) {
      message.error(e.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  const columns = [
    { title: '번호', dataIndex: 'no', width: 70, responsive: ['md'] },
    { title: '제목', dataIndex: 'Title', align: 'left', render: (v, r) => <Button type="link" style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => openEdit(r.no)}>{v}</Button> },
    { title: '작성일', dataIndex: 'Write_DT', width: 150, render: (v) => (v ? (isMobile ? v.slice(2, 10) : v) : '-') },
    {
      title: '', key: 'actions', width: isMobile ? 84 : 210, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          {!isMobile && <Button size="small" icon={<EyeOutlined />} href={`/update/${r.no}`} target="_blank">보기</Button>}
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r.no)} aria-label="수정">{!isMobile && '수정'}</Button>
          <Popconfirm title="이 업데이트 내역을 삭제할까요?" okText="삭제" okButtonProps={{ danger: true }} cancelText="취소" onConfirm={() => remove(r.no)}>
            <Button size="small" danger icon={<DeleteOutlined />} aria-label="삭제">{!isMobile && '삭제'}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <Card size="small" title="하단 버전 표기" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input addonBefore="V" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="예) 2.3.0" style={{ width: 200 }} maxLength={30} onPressEnter={saveVersion} />
          <Button type="primary" icon={<SaveOutlined />} loading={versionSaving} disabled={!version.trim() || version === savedVersion} onClick={saveVersion}>저장</Button>
          <Text type="secondary">모든 화면 왼쪽 아래에 표시됩니다.</Text>
        </Space>
      </Card>

      <Card
        size="small" title="업데이트 내역"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNew}>새 업데이트</Button>}
      >
        <Table rowKey="no" size="small" loading={loading} columns={columns} dataSource={items} pagination={{ pageSize: 15, hideOnSinglePage: true }} />
        <Text type="secondary" style={{ fontSize: 12 }}>메인 화면에는 최근 5건이 보입니다.</Text>
      </Card>

      <Modal
        open={!!editing} onCancel={() => setEditing(null)} title={editing?.no ? '업데이트 내역 수정' : '새 업데이트'}
        width={isMobile ? '100%' : 860} style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : { top: 32 }}
        destroyOnClose maskClosable={false}
        footer={[
          <Button key="c" onClick={() => setEditing(null)}>취소</Button>,
          <Button key="s" type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>{editing?.no ? '수정' : '등록'}</Button>,
        ]}
      >
        {editing && (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <div className="upd-form-row">
              <Input size="large" placeholder="제목 (예: [업데이트] 근무시간 입력 페이지 추가)" value={editing.title} maxLength={100} showCount
                onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} />
              <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" value={editing.write_dt} allowClear={false}
                onChange={(v) => setEditing((s) => ({ ...s, write_dt: v }))} size="large" />
            </div>
            <RichEditor
              value={editing.content} height={isMobile ? 300 : 380}
              onChange={(html) => setEditing((s) => (s ? { ...s, content: html } : s))}
              placeholder="바뀐 내용을 적어 주세요. 이미지는 붙여넣기(Ctrl+V)나 끌어놓기로 넣을 수 있습니다."
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default UpdatesAdminPage;
